'use client';

import React, { forwardRef, useId } from 'react';
import { AlignLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  FieldShellLeadIcon,
  fieldShellWrapperClass,
  fieldShellWrapperErrorClass,
} from '@/components/ui/field-shell';

const shellRowClass = 'min-h-[5.5rem] items-start gap-3 py-2.5';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  wrapperClassName?: string;
  shellClassName?: string;
  labelClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      wrapperClassName,
      shellClassName,
      labelClassName,
      label,
      error,
      helperText,
      id: externalId,
      disabled,
      rows = 3,
      ...props
    },
    ref,
  ) => {
    const autoId = useId();
    const id = externalId ?? autoId;
    const errorId = `${id}-error`;
    const helperId = `${id}-helper`;

    return (
      <div className={cn('flex flex-col gap-1.5', wrapperClassName)}>
        {label ? (
          <label htmlFor={id} className={cn('text-sm font-medium text-gray-700 dark:text-gray-300', labelClassName)}>
            {label}
          </label>
        ) : null}

        <div
          className={cn(
            fieldShellWrapperClass,
            shellRowClass,
            shellClassName,
            error && fieldShellWrapperErrorClass,
            disabled && 'opacity-60',
          )}
        >
          <FieldShellLeadIcon Icon={AlignLeft} className="mt-0.5" />
          <textarea
            ref={ref}
            id={id}
            rows={rows}
            disabled={disabled}
            aria-invalid={!!error}
            aria-describedby={
              [error ? errorId : null, helperText ? helperId : null].filter(Boolean).join(' ') || undefined
            }
            className={cn(
              'min-h-[4.5rem] min-w-0 flex-1 resize-y border-0 bg-transparent p-0 font-medium leading-snug text-primary-900 shadow-none ring-0 outline-none',
              'text-sm placeholder:font-normal placeholder:text-gray-500 dark:text-primary-50 dark:placeholder:text-gray-400',
              'focus:ring-0 focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-60',
              className,
            )}
            {...props}
          />
        </div>

        {error ? (
          <p id={errorId} className="text-xs text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        ) : null}
        {!error && helperText ? (
          <p id={helperId} className="text-xs text-gray-500 dark:text-gray-400">
            {helperText}
          </p>
        ) : null}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
